<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import validations, { getLabelTitleErrorMessage } from './validations';
import { getRandomColor } from 'dashboard/helper/labelColor';
import { useVuelidate } from '@vuelidate/core';
import { ref } from 'vue';
import { Editor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { HtmlToMarkdownConverter } from './HtmlToMarkdownConverter.js';
import { MarkdownToHtmlConverter } from './MarkdownToHtmlConverter.js';

export default {
  components: {
    NextButton,
    EditorContent,
  },
  props: {
    prefillTitle: {
      type: String,
      default: '',
    },
    prefillDescription: {
      type: String,
      default: '',
    },
  },
  emits: ['close'],
  setup() {
    const descriptionEditor = ref(null);

    return {
      v$: useVuelidate(),
      descriptionEditor,
    };
  },
  data() {
    return {
      color: '#000',
      description: '',
      title: '',
      showOnSidebar: true,
      syncToChannel: false,
    };
  },
  validations,
  computed: {
    ...mapGetters({
      uiFlags: 'labels/getUIFlags',
    }),
    labelTitleErrorMessage() {
      const errorMessage = getLabelTitleErrorMessage(this.v$);
      return this.$t(errorMessage);
    },
  },
  mounted() {
    this.color = getRandomColor();
    this.title = this.prefillTitle.toLowerCase();
    this.initializeDescriptionEditor();
  },
  beforeUnmount() {
    if (this.descriptionEditor) {
      this.descriptionEditor.destroy();
    }
  },
  methods: {
    initializeDescriptionEditor() {
      // Convert markdown to HTML if we have a prefilled description
      let initialContent = '<p></p>';
      if (this.prefillDescription) {
        initialContent = MarkdownToHtmlConverter.convertToHtml(
          this.prefillDescription
        );
        this.description = this.prefillDescription;
      }

      this.descriptionEditor = new Editor({
        content: initialContent,
        extensions: [StarterKit],
        editorProps: {
          attributes: {
            class:
              'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none',
          },
        },
        onUpdate: ({ editor }) => {
          // Convert HTML content to markdown for storage
          const htmlContent = editor.getHTML();
          const converter = new HtmlToMarkdownConverter();
          this.description = converter.convertToMarkdown(htmlContent);
        },
      });
    },
    onClose() {
      this.$emit('close');
    },
    resetForm() {
      this.color = getRandomColor();
      this.description = this.prefillDescription || '';
      this.title = this.prefillTitle.toLowerCase();
      this.showOnSidebar = true;
      this.syncToChannel = false;
      this.v$?.$reset?.();
      if (this.descriptionEditor) {
        const initialContent = this.prefillDescription
          ? MarkdownToHtmlConverter.convertToHtml(this.prefillDescription)
          : '<p></p>';
        this.descriptionEditor.commands.setContent(initialContent, false);
      }
    },
    onCancel() {
      this.resetForm();
      this.onClose();
    },
    async addLabel() {
      try {
        await this.$store.dispatch('labels/create', {
          color: this.color,
          description: this.description,
          title: this.title.toLowerCase(),
          show_on_sidebar: this.showOnSidebar,
          sync_to_channel: this.syncToChannel,
        });
        useAlert(this.$t('LABEL_MGMT.ADD.API.SUCCESS_MESSAGE'));
        this.resetForm();
        this.onClose();
      } catch (error) {
        const errorMessage =
          error.message || this.$t('LABEL_MGMT.ADD.API.ERROR_MESSAGE');
        useAlert(errorMessage);
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="$t('LABEL_MGMT.ADD.TITLE')"
      :header-content="$t('LABEL_MGMT.ADD.DESC')"
    />
    <form class="flex flex-wrap mx-0" @submit.prevent="addLabel">
      <woot-input
        v-model="title"
        :class="{ error: v$.title.$error }"
        class="w-full label-name--input"
        :label="$t('LABEL_MGMT.FORM.NAME.LABEL')"
        :placeholder="$t('LABEL_MGMT.FORM.NAME.PLACEHOLDER')"
        :error="labelTitleErrorMessage"
        data-testid="label-title"
        @input="v$.title.$touch"
        @blur="v$.title.$touch"
      />

      <div class="w-full">
        <label class="block text-sm font-medium text-slate-800 mb-2">
          {{ $t('LABEL_MGMT.FORM.DESCRIPTION.LABEL') }}
        </label>
        <div class="border border-slate-200 rounded-md overflow-hidden">
          <EditorContent
            v-if="descriptionEditor"
            :editor="descriptionEditor"
            class="description-editor"
          />
        </div>
      </div>

      <div class="w-full">
        <label>
          {{ $t('LABEL_MGMT.FORM.COLOR.LABEL') }}
          <woot-color-picker v-model="color" />
        </label>
      </div>
      <div class="flex items-center w-full gap-2">
        <input v-model="showOnSidebar" type="checkbox" :value="true" />
        <label for="show_on_sidebar">
          {{ $t('LABEL_MGMT.FORM.SHOW_ON_SIDEBAR.LABEL') }}
        </label>
      </div>
      <div class="flex items-center w-full gap-2">
        <input v-model="syncToChannel" type="checkbox" :value="true" />
        <label for="sync_to_channel">
          {{ $t('LABEL_MGMT.FORM.SYNC_TO_CHANNEL.LABEL') }}
        </label>
      </div>
      <div class="flex items-center justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('LABEL_MGMT.FORM.CANCEL')"
          @click.prevent="onCancel"
        />
        <NextButton
          type="submit"
          data-testid="label-submit"
          :label="$t('LABEL_MGMT.FORM.CREATE')"
          :disabled="v$.title.$invalid || uiFlags.isCreating"
          :is-loading="uiFlags.isCreating"
        />
      </div>
    </form>
  </div>
</template>

<style lang="scss" scoped>
// Label API supports only lowercase letters
.label-name--input {
  ::v-deep {
    input {
      text-transform: lowercase;
    }
  }
}

.description-editor {
  min-height: 120px;
}

::v-deep .description-editor .tiptap {
  outline: none;
  padding: 12px;
  min-height: 120px;
  font-size: 14px;
  line-height: 1.5;
}

::v-deep .description-editor .tiptap p {
  margin: 0.5em 0;
}

::v-deep .description-editor .tiptap:first-child {
  margin-top: 0;
}

::v-deep .description-editor .tiptap ul,
::v-deep .description-editor .tiptap ol {
  padding: 0 1rem;
  margin: 1rem 0;
}

::v-deep .description-editor .tiptap h1,
::v-deep .description-editor .tiptap h2,
::v-deep .description-editor .tiptap h3,
::v-deep .description-editor .tiptap h4,
::v-deep .description-editor .tiptap h5,
::v-deep .description-editor .tiptap h6 {
  line-height: 1.1;
  margin-top: 1rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

::v-deep .description-editor .tiptap h1 {
  font-size: 1.25rem;
}

::v-deep .description-editor .tiptap h2 {
  font-size: 1.125rem;
}

::v-deep .description-editor .tiptap h3 {
  font-size: 1rem;
}

::v-deep .description-editor .tiptap code {
  background-color: rgba(97, 97, 97, 0.1);
  border-radius: 0.25rem;
  color: #616161;
  font-size: 0.85rem;
  padding: 0.125em 0.25em;
}

::v-deep .description-editor .tiptap strong {
  font-weight: 600;
}

::v-deep .description-editor .tiptap em {
  font-style: italic;
}

::v-deep .description-editor .tiptap blockquote {
  border-left: 3px solid rgba(13, 13, 13, 0.1);
  margin: 1rem 0;
  padding-left: 1rem;
}

::v-deep .description-editor .tiptap p.is-editor-empty:first-child::before {
  content: 'Enter a description for this label...';
  float: left;
  color: #adb5bd;
  pointer-events: none;
  height: 0;
}
</style>
